import { useState, useEffect, useRef } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { addMinutes } from 'date-fns';
import { NotificationService } from '../../notifications/services/NotificationService';

// Singleton Audio Instance
// This ensures only ONE audio player exists for the entire app session.
const globalAudio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
globalAudio.loop = true;

const useReminderSystem = (currentUser) => {
    const [activeAlarmTask, setActiveAlarmTask] = useState(null);
    const [isMuted, setIsMuted] = useState(() => localStorage.getItem('alarm_muted') === 'true');
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    const [dismissedTasks, setDismissedTasks] = useState(new Set());

    const toggleMute = () => {
        const newState = !isMuted;
        setIsMuted(newState);
        localStorage.setItem('alarm_muted', newState);
        if (newState) {
            globalAudio.pause();
            globalAudio.currentTime = 0;
            globalAudio.volume = 0; // Failsafe
        } else {
            globalAudio.volume = 1;
        }
    };

    // Sync Mute across tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'alarm_muted') {
                const newState = e.newValue === 'true';
                setIsMuted(newState);
                if (newState) {
                    globalAudio.pause();
                    globalAudio.currentTime = 0;
                    globalAudio.volume = 0;
                } else {
                    globalAudio.volume = 1;
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Initialize Audio Unlock
    useEffect(() => {
        // Ensure volume matches initial state
        globalAudio.volume = isMuted ? 0 : 1;

        // Try to unlock audio context on any interaction
        const unlockAudio = () => {
            if (isMuted) return; // Don't play if muted
            globalAudio.play().then(() => {
                globalAudio.pause();
                globalAudio.currentTime = 0;
                setAudioUnlocked(true);
                document.removeEventListener('click', unlockAudio);
                document.removeEventListener('keydown', unlockAudio);
            }).catch(e => console.log("Audio unlock waiting...", e));
        };

        document.addEventListener('click', unlockAudio);
        document.addEventListener('keydown', unlockAudio);

        return () => {
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
        };
    }, [isMuted]);

    // ONE tasks listener per signed-in user. It used to depend on [currentUser,
    // activeAlarmTask, dismissedTasks]: the user object is replaced every few seconds
    // (presence heartbeat), so the listener was torn down and the WHOLE tasks node
    // re-downloaded each time — 256 times in 10 minutes on two office PCs (database
    // profile, 27 Oct 2026). The alarm state is read through refs instead, and the
    // last data is re-checked every 30 s so a task still alarms when it falls due.
    const uid = currentUser?.uid || '';
    const alarmRef = useRef(null);
    const dismissedRef = useRef(dismissedTasks);
    const tasksDataRef = useRef(null);
    alarmRef.current = activeAlarmTask;
    dismissedRef.current = dismissedTasks;

    const checkTasks = () => {
        const data = tasksDataRef.current;
        const active = alarmRef.current;
        if (!data) { if (active) stopAlarm(); return; }
        const now = Date.now();
        // Check if active alarm task is still valid
        if (active) {
            const currentTask = data[active.id];
            if (!currentTask || currentTask.status === 'completed' || currentTask.reminderTriggered) {
                stopAlarm();
            }
        }
        for (const [taskId, task] of Object.entries(data)) {
            if (task.assignedTo !== uid && task.assignedTo !== 'self') continue;
            if (task.reminderTriggered || task.status === 'completed') continue;
            if (dismissedRef.current.has(taskId)) continue; // Skip locally dismissed tasks
            // Check Reminder or Due Timestamp
            if ((task.reminderTimestamp && task.reminderTimestamp <= now) ||
                (task.dueTimestamp && task.dueTimestamp <= now)) {
                if (!alarmRef.current) {
                    alarmRef.current = { id: taskId, ...task };
                    triggerAlarm({ id: taskId, ...task });
                }
            }
        }
    };

    useEffect(() => {
        if (!uid) return undefined;
        const unsubscribe = onValue(ref(db, 'tasks'), (snapshot) => {
            tasksDataRef.current = snapshot.exists() ? snapshot.val() : null;
            checkTasks();
        });
        const tick = setInterval(checkTasks, 30_000);
        return () => { unsubscribe(); clearInterval(tick); tasksDataRef.current = null; };
    }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

    // A snooze / postpone / complete changes the local state: re-check what we have.
    useEffect(() => { checkTasks(); }, [activeAlarmTask, dismissedTasks]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerAlarm = async (task) => {
        setActiveAlarmTask(task);

        // Check localStorage directly to avoid stale state
        const isMutedNow = localStorage.getItem('alarm_muted') === 'true';

        // 1. Play Audio (if not muted)
        if (!isMutedNow) {
            try {
                // globalAudio.volume = 1;
                // globalAudio.currentTime = 0;
                // await globalAudio.play();
            } catch (e) {
                console.error("Audio play failed (likely blocked):", e);
            }
        } else {
            // Ensure it's silent if muted
            globalAudio.volume = 0;
            globalAudio.pause();
        }

        // 2. Vibrate (Mobile)
        if (navigator.vibrate && !isMutedNow) {
            navigator.vibrate([1000, 500, 1000, 500, 1000]); // SOS pattern-ish
        }

        // 3. Notification (System)
        if (Notification.permission === 'granted') {
            new Notification(`Task Due: ${task.title}`);
        }

        // 4. In-App Notification
        NotificationService.send(currentUser.uid, {
            type: 'system_alert',
            title: 'Task Due',
            message: `Task '${task.title}' is now due`,
            link: `/dashboard/todo`,
            meta: { taskId: task.id }
        });
    };

    const stopSoundOnly = () => {
        globalAudio.pause();
        globalAudio.currentTime = 0;
        if (navigator.vibrate) navigator.vibrate(0);
    };

    const stopAlarm = () => {
        stopSoundOnly();
        setActiveAlarmTask(null);
    };

    const handleSnooze = async (minutes) => {
        if (!activeAlarmTask) return;
        const taskId = activeAlarmTask.id;
        setDismissedTasks(prev => new Set(prev).add(taskId)); // Locally dismiss
        stopAlarm();

        const newReminderTime = addMinutes(new Date(), minutes).getTime();
        await update(ref(db, `tasks/${taskId}`), {
            reminderTimestamp: newReminderTime,
            reminderTriggered: false
        });

        // Clear from dismissed after update (optional, but good for safety)
        setTimeout(() => {
            setDismissedTasks(prev => {
                const next = new Set(prev);
                next.delete(taskId);
                return next;
            });
        }, 1000);
    };

    const handlePostpone = async (newTimestamp) => {
        if (!activeAlarmTask) return;
        const taskId = activeAlarmTask.id;
        setDismissedTasks(prev => new Set(prev).add(taskId));
        stopAlarm();

        await update(ref(db, `tasks/${taskId}`), {
            dueTimestamp: newTimestamp,
            reminderTimestamp: newTimestamp,
            reminderTriggered: false
        });

        setTimeout(() => {
            setDismissedTasks(prev => {
                const next = new Set(prev);
                next.delete(taskId);
                return next;
            });
        }, 1000);
    };

    const handleComplete = async (taskId) => {
        stopSoundOnly(); // Stop immediately
        setDismissedTasks(prev => new Set(prev).add(taskId));
        setActiveAlarmTask(null); // Close overlay immediately

        await update(ref(db, `tasks/${taskId}`), {
            status: 'completed',
            reminderTriggered: true
        });
    };

    return { activeAlarmTask, handleSnooze, handlePostpone, handleComplete, audioUnlocked, isMuted, toggleMute, stopSoundOnly };
};

export default useReminderSystem;
